/* enginora smart: accessible, progressively enhanced two-image hero. */
(() => {
  'use strict';
  const namespace = '__enginoraHeroSlideshow';
  if (window[namespace]) {
    window[namespace].mount(document);
    return;
  }
  const instances = new WeakMap();
  const selector = '[data-enginora-hero]';

  function init(root) {
    if (instances.has(root)) return;
    const slides = Array.from(root.querySelectorAll('[data-hero-slide]'));
    const controls = root.querySelector('[data-hero-controls]');
    if (slides.length < 2 || !controls) return;
    const previous = root.querySelector('[data-hero-prev]');
    const next = root.querySelector('[data-hero-next]');
    const pause = root.querySelector('[data-hero-pause-button]');
    const pauseLabel = root.querySelector('[data-hero-pause-label]');
    const pauseSymbol = root.querySelector('[data-hero-pause-symbol]');
    const dots = Array.from(root.querySelectorAll('[data-hero-dot]'));
    const status = root.querySelector('[data-hero-status]');
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const controller = new AbortController();
    const delay = Math.max(6000, Number(root.dataset.heroDelay) || 6000);
    let index = 0;
    let timer = null;
    let userPaused = motion.matches;
    let focusPaused = root.contains(document.activeElement);
    let pointerPaused = false;
    let visible = true;
    let observer = null;
    let gesture = null;
    let suppressImageClickUntil = 0;

    function stop() {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
    }

    function canRun() {
      return root.isConnected && !gesture && !userPaused && !focusPaused && !pointerPaused &&
        visible && !document.hidden;
    }

    function schedule() {
      stop();
      if (canRun()) timer = window.setTimeout(() => show(index + 1, false), delay);
    }

    function updatePause() {
      pauseLabel.textContent = userPaused ? root.dataset.heroPlay : root.dataset.heroPause;
      pauseSymbol.dataset.state = userPaused ? 'play' : 'pause';
      pause.dataset.paused = String(userPaused);
    }

    function show(target, manual, entryOffset = 0) {
      stop();
      root.style.setProperty('--hero-enter-x', motion.matches ? '0px' : entryOffset + 'px');
      const targetIndex = ((target % slides.length) + slides.length) % slides.length;
      if (manual) userPaused = true;
      if (targetIndex !== index) {
        index = targetIndex;
        slides.forEach((slide, position) => {
          const active = position === index;
          slide.hidden = !active;
          slide.inert = !active;
          slide.setAttribute('aria-hidden', String(!active));
          slide.classList.toggle('is-active', active);
        });
        dots.forEach((dot, position) => dot.setAttribute('aria-current', String(position === index)));
      }
      if (status) status.textContent = manual ? slides[index].dataset.heroLabel : '';
      updatePause();
      schedule();
    }

    function listen(target, type, handler, options = {}) {
      target.addEventListener(type, handler, { ...options, signal: controller.signal });
    }

    function clearGesture() {
      if (!gesture) return null;
      const completed = gesture;
      gesture = null;
      completed.image.removeAttribute('data-hero-dragging');
      completed.image.style.removeProperty('--hero-drag-x');
      if (completed.image.hasPointerCapture?.(completed.pointerId)) {
        completed.image.releasePointerCapture(completed.pointerId);
      }
      return completed;
    }

    listen(root, 'pointerdown', (event) => {
      if (event.isPrimary === false) {
        clearGesture();
        schedule();
        return;
      }
      const image = event.target.closest('.enginora-hero-slide-image');
      if (!image || !root.contains(image) ||
          (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (gesture) clearGesture();
      gesture = { image, pointerId:event.pointerId, x:event.clientX, y:event.clientY, dx:0, axis:null };
      stop();
    }, { passive:true });
    listen(window, 'pointermove', (event) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      if (gesture.axis === null) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx) * 1.15) gesture.axis = 'y';
        else if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy) * 1.15) {
          gesture.axis = 'x';
          gesture.image.setPointerCapture?.(event.pointerId);
          gesture.image.setAttribute('data-hero-dragging', 'true');
        }
      }
      if (gesture.axis !== 'x') return;
      gesture.dx = dx;
      if (event.cancelable) event.preventDefault();
      if (!motion.matches) {
        const limit = gesture.image.clientWidth * 0.35;
        gesture.image.style.setProperty('--hero-drag-x', Math.max(-limit, Math.min(limit, dx)) + 'px');
      }
    }, { passive:false });
    listen(window, 'pointerup', (event) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      const completed = clearGesture();
      if (completed.axis === 'x') {
        suppressImageClickUntil = Date.now() + 500;
        const threshold = Math.min(96, Math.max(40, completed.image.clientWidth * 0.13));
        if (Math.abs(completed.dx) >= threshold) {
          const rtl = window.getComputedStyle(root).direction === 'rtl';
          const forward = rtl ? completed.dx > 0 : completed.dx < 0;
          show(index + (forward ? 1 : -1), true, completed.dx < 0 ? 22 : -22);
          return;
        }
      }
      schedule();
    });
    listen(window, 'pointercancel', (event) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      clearGesture();
      schedule();
    });
    listen(root, 'click', (event) => {
      if (Date.now() < suppressImageClickUntil && event.target.closest('.enginora-hero-slide-image')) {
        suppressImageClickUntil = 0;
        event.preventDefault();
        event.stopPropagation();
      }
    }, { capture:true });
    listen(root, 'dragstart', (event) => {
      if (event.target.closest('.enginora-hero-slide-image')) event.preventDefault();
    });

    listen(previous, 'click', () => show(index - 1, true));
    listen(next, 'click', () => show(index + 1, true));
    dots.forEach((dot) => listen(dot, 'click', () => show(Number(dot.dataset.heroDot), true)));
    listen(pause, 'click', () => {
      userPaused = !userPaused;
      // An explicit Play request may resume while the pointer/focus remains on this control.
      if (!userPaused) {
        focusPaused = false;
        pointerPaused = false;
      }
      updatePause();
      schedule();
    });
    listen(root, 'pointerenter', (event) => {
      if (event.pointerType === 'touch') return;
      pointerPaused = true;
      stop();
    });
    listen(root, 'pointerleave', () => {
      pointerPaused = false;
      schedule();
    });
    listen(root, 'focusin', () => {
      focusPaused = true;
      stop();
    });
    listen(root, 'focusout', (event) => {
      if (event.relatedTarget && root.contains(event.relatedTarget)) return;
      focusPaused = false;
      schedule();
    });
    listen(root, 'keydown', (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey ||
          event.target.closest('button,a,input,select,textarea,[contenteditable="true"]')) return;
      const rtl = window.getComputedStyle(root).direction === 'rtl';
      let target = null;
      if (event.key === 'ArrowRight') target = index + (rtl ? -1 : 1);
      if (event.key === 'ArrowLeft') target = index + (rtl ? 1 : -1);
      if (event.key === 'Home') target = 0;
      if (event.key === 'End') target = slides.length - 1;
      if (target === null) return;
      event.preventDefault();
      show(target, true);
    });
    listen(document, 'visibilitychange', schedule);
    const onMotionChange = () => {
      if (motion.matches) userPaused = true;
      updatePause();
      schedule();
    };
    motion.addEventListener('change', onMotionChange);
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        visible = entries[0].isIntersecting;
        schedule();
      }, { threshold: 0.2 });
      observer.observe(root);
    }

    const destroy = () => {
      stop();
      clearGesture();
      controller.abort();
      motion.removeEventListener('change', onMotionChange);
      if (observer) observer.disconnect();
      instances.delete(root);
    };
    instances.set(root, { destroy });
    controls.hidden = false;
    root.dataset.heroReady = 'true';
    updatePause();
    schedule();
  }

  function rootsWithin(scope) {
    const roots = Array.from(scope.querySelectorAll(selector));
    if (scope.matches && scope.matches(selector)) roots.unshift(scope);
    return roots;
  }

  function mount(scope) {
    rootsWithin(scope).forEach(init);
  }

  window[namespace] = { mount };
  mount(document);
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    rootsWithin(event.target).forEach((root) => {
      const instance = instances.get(root);
      if (instance) instance.destroy();
    });
  });
})();
